import { type HistoricoAndamento } from '@/core/api/sicom-api/servicos/kanban/kanban-servicos.model';

/**
 * Natureza de uma linha do histórico. O backend não a informa — ela se deduz de quais campos vieram
 * —, e é ela que decide o ícone e a cor do marcador na timeline. Enum, e não string solta: os três
 * casos são fechados, e cada um tem um par ícone/cor que precisa combinar.
 */
export enum EEventoHistorico {
    /** Primeira aparição do protocolo no quadro: tem destino, mas não tem origem de onde saiu. */
    ENTRADA = 'entrada',
    /** Andou de uma coluna para outra. */
    MOVIMENTO = 'movimento',
    /** Sem troca de coluna — sobrou o comentário (ou só o encaminhamento). */
    ANOTACAO = 'anotacao'
}

/** Uma linha do histórico já no formato que a timeline desenha. */
export interface EventoHistoricoTimeline {
    tipo: EEventoHistorico;
    /** Classe do ícone do marcador (`pi pi-*`). */
    icone: string;
    /**
     * O que aconteceu, em texto. Continua sendo o rótulo desenhado nos eventos SEM troca de coluna, e
     * o rótulo acessível da linha de badges nos demais — dois `span` e uma seta não se leem em voz
     * alta como uma frase.
     */
    titulo: string;
    /** Coluna de onde saiu. Ausente na entrada no quadro, que não veio de lugar nenhum. */
    statusOrigem?: string;
    /** Coluna onde parou. Ausente quando o evento não trocou de coluna (comentário/encaminhamento). */
    statusDestino?: string;
    /** Data no formato dd/MM/aaaa, ou o texto cru quando não é data válida. */
    data: string;
    /** Hora no formato HH:mm; vazia quando não há data válida. */
    hora: string;
    /** Quem registrou. Nunca vazio — sem identificação vira o rótulo de desconhecido. */
    usuario: string;
    anotacao?: string;
    encaminhadoPara?: string;
    /**
     * Id da anotação que guarda o e-mail enviado ao médico. Presente só nas linhas que TÊM e-mail —
     * é a presença dele que decide se o card oferece o botão de ver a mensagem.
     */
    idAnotacaoComEmail?: number;
}

const USUARIO_DESCONHECIDO = 'Usuário não identificado';

/**
 * Nome reduzido à forma comparável: sem acento, sem caixa e sem espaço sobrando.
 *
 * O histórico identifica quem agiu pelo NOME, e a foto é indexada por id de usuário — casar os dois
 * é comparar strings vindas de campos diferentes do banco. "ANA SOUZA" e "Ana  Souza" são a mesma
 * pessoa, e comparação crua deixaria o card sem foto por causa de um espaço a mais.
 */
export function chaveDeNome(nome?: string): string {
    return (nome ?? '')
        .normalize('NFD')
        .replace(/\p{Diacritic}/gu, '')
        .trim()
        .toLowerCase()
        .replace(/\s+/g, ' ');
}

/**
 * Ícone do marcador por natureza do evento. A COR não vive aqui: ela é um modificador derivado do
 * próprio `tipo` no SCSS (`.kanban-historico-marcador--<tipo>`) — mapear domínio para nome de classe
 * de CSS neste arquivo amarraria a regra de negócio ao framework de estilo do momento.
 */
const ICONE: Record<EEventoHistorico, string> = {
    [EEventoHistorico.ENTRADA]: 'pi pi-sign-in',
    [EEventoHistorico.MOVIMENTO]: 'pi pi-arrow-right',
    [EEventoHistorico.ANOTACAO]: 'pi pi-comment'
};

/** Data e hora separadas, porque a timeline as mostra em linhas diferentes. */
function separarDataHora(valor?: string): { data: string; hora: string } {
    const texto = (valor ?? '').trim();
    if (!texto) {
        return { data: '', hora: '' };
    }

    const data = new Date(texto);
    // Texto que não é data vai cru para a linha da data, como já faz `formatarDataTramitacao`:
    // esconder o valor seria pior do que mostrá-lo como veio.
    if (Number.isNaN(data.getTime())) {
        return { data: texto, hora: '' };
    }

    return {
        data: new Intl.DateTimeFormat('pt-BR', { day: '2-digit', month: '2-digit', year: 'numeric' }).format(data),
        hora: new Intl.DateTimeFormat('pt-BR', { hour: '2-digit', minute: '2-digit' }).format(data)
    };
}

/** Natureza do evento a partir dos campos que vieram preenchidos. */
function tipoDoEvento(linha: HistoricoAndamento): EEventoHistorico {
    if (!linha.statusDestino) {
        return EEventoHistorico.ANOTACAO;
    }

    return linha.statusOrigem ? EEventoHistorico.MOVIMENTO : EEventoHistorico.ENTRADA;
}

/** Frase do cabeçalho do evento, a que responde "o que aconteceu aqui". */
function tituloDoEvento(linha: HistoricoAndamento, tipo: EEventoHistorico): string {
    switch (tipo) {
        case EEventoHistorico.ENTRADA:
            return `Entrou no quadro em ${linha.statusDestino}`;
        case EEventoHistorico.MOVIMENTO:
            return `${linha.statusOrigem} → ${linha.statusDestino}`;
        default:
            // Linha com e-mail nao mostra o texto no card — so o botao. Chamar isso de "Comentario"
            // deixaria um card anunciando um comentario que nao esta em lugar nenhum.
            if (linha.temEmail) {
                return 'Aviso ao médico';
            }
            // Encaminhamento sem troca de coluna é um evento de verdade, e "Comentário" o
            // descreveria errado — o card mudou de dono, não ganhou um recado.
            return linha.usuarioDestino ? 'Encaminhamento' : 'Comentário';
    }
}

/**
 * Converte o histórico cru do backend nos eventos que a timeline desenha, **do mais recente para o
 * mais antigo**.
 *
 * O backend entrega em ordem cronológica (o mais antigo primeiro), que é a ordem em que os fatos
 * aconteceram — mas não a ordem em que interessam: quem abre o histórico quer saber onde o protocolo
 * está AGORA, e essa é a última linha. Com a lista crescendo, a tramitação mais nova ia para o fim e
 * exigia rolar o diálogo inteiro para chegar nela.
 *
 * Inverte, e não ordena por data: a ordenação é do backend, e refazê-la aqui por um `dataHora` que
 * pode vir ausente ou malformado trocaria uma ordem conhecida por um palpite.
 *
 * Função pura, fora do componente: o que ela faz é decidir texto e ícone a partir de quais campos
 * vieram — regra de apresentação, testável sem montar o diálogo inteiro.
 */
export function paraEventosDeTimeline(historico: HistoricoAndamento[]): EventoHistoricoTimeline[] {
    return historico
        .map((linha) => {
            const tipo = tipoDoEvento(linha);
            const { data, hora } = separarDataHora(linha.dataHora);

            return {
                tipo,
                icone: ICONE[tipo],
                titulo: tituloDoEvento(linha, tipo),
                statusOrigem: linha.statusOrigem?.trim() || undefined,
                statusDestino: linha.statusDestino?.trim() || undefined,
                data,
                hora,
                usuario: (linha.usuario ?? '').trim() || USUARIO_DESCONHECIDO,
                anotacao: linha.anotacao?.trim() || undefined,
                encaminhadoPara: linha.usuarioDestino?.trim() || undefined,
                // Só quando as duas coisas vêm: sem id não há o que buscar, e sem a flag não há o que
                // mostrar. Um botão que abre um frame vazio é pior do que botão nenhum.
                idAnotacaoComEmail: linha.temEmail && linha.idAnotacao != null ? linha.idAnotacao : undefined
            };
        })
        .reverse();
}
